import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { api, type DocumentSummary, type HomeResponse } from "../../lib/api";
import { useUser } from "../../lib/auth";
import { formatDate } from "../../lib/format";
import {
  Bar,
  Card,
  Empty,
  ErrorNote,
  Metric,
  PageHeader,
  ReviewBadge,
  Spinner,
  StatusPill,
} from "../../components/ui";

export function HomeScreen() {
  const user = useUser();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ["home"],
    queryFn: () => api.get<HomeResponse>("/home"),
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  return (
    <div className="flex flex-col gap-[18px]">
      <PageHeader
        eyebrow="SGN Operational Technology"
        title={`${data.greeting}, ${data.firstName}`}
        serifSize="h1home"
        subtitle={`Signed in as ${user.roleArticleLabel}`}
        actions={
          <>
            <Link to="/documents" className="btn-secondary no-underline">
              Browse library
            </Link>
            <Link to="/assistant" className="btn-dark no-underline">
              Ask Compendium Discover
            </Link>
          </>
        }
      />

      {data.documentCount === 0 && (
        <Card>
          <Empty
            title="The compendium is empty"
            detail={
              user.canUpload
                ? "Drop documents into the knowledge-base folder, or upload them from the Ingestion screen. Every figure on this page is derived from what has actually been indexed, so nothing is shown until documents arrive."
                : "No documents have been indexed yet. A Network Control Engineer or Manager needs to load the knowledge base."
            }
            action={
              user.canUpload ? (
                <Link to="/ingestion" className="btn-green mt-2 no-underline">
                  Go to ingestion
                </Link>
              ) : undefined
            }
          />
        </Card>
      )}

      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">
        {data.kpis.map((kpi) => (
          <Metric
            key={kpi.label}
            label={kpi.label}
            value={kpi.value}
            trend={kpi.trend}
            tone={kpi.tone}
          />
        ))}
      </div>

      {/* The dark "Ask the compendium" hero. */}
      <section className="flex flex-col gap-[14px] rounded-panel bg-ink px-6 py-[22px] text-pearl">
        <div className="flex items-center gap-[7px]">
          <span className="h-[7px] w-[7px] rounded-full bg-accent" aria-hidden />
          <span className="text-micro font-extrabold uppercase tracking-[0.13em] text-accent">
            Ask the compendium
          </span>
        </div>
        <h2 className="m-0 max-w-[620px] font-display text-h2serif font-normal leading-[1.25]">
          What do you need to know from the OT document set?
        </h2>
        {data.suggestedQuestions.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {data.suggestedQuestions.map((question) => (
              <button
                key={question}
                onClick={() => navigate(`/assistant?q=${encodeURIComponent(question)}`)}
                className="rounded-full border border-night-500 bg-night-800 px-[15px] py-2 text-left text-base+ text-pearl transition-colors hover:border-accent"
              >
                {question}
              </button>
            ))}
          </div>
        ) : (
          <Link to="/assistant" className="btn-green self-start no-underline">
            Open Compendium Discover
          </Link>
        )}
      </section>

      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(330px,1fr))]">
        <Card
          title={user.canReview || user.canUpload ? "Needs your attention" : "Recently published"}
          action={
            user.canUpload ? (
              <Link to="/tasks" className="text-base font-bold no-underline">
                View all
              </Link>
            ) : undefined
          }
          padded={false}
        >
          <DocumentRows
            documents={
              user.canReview || user.canUpload
                ? data.needsAttention
                : data.recentlyPublished
            }
            empty="Nothing is waiting on you."
          />
        </Card>

        <Card title="Ingestion and index health" collapsible storageKey="index-health-home" defaultOpen={false}>
          {data.indexHealth.length === 0 ? (
            <p className="text-base+ text-muted">No documents indexed yet.</p>
          ) : (
            data.indexHealth.map((bar) => <Bar key={bar.label} {...bar} />)
          )}
          <p className="mt-3 border-t border-line-soft pt-3 text-sm leading-[1.6] text-muted-soft">
            Every figure here is computed from the database. Nothing on this
            screen is a placeholder.
          </p>
        </Card>
      </div>

      {(user.canReview || user.canUpload) && data.recentlyPublished.length > 0 && (
        <Card title="Recently published" padded={false}>
          <DocumentRows documents={data.recentlyPublished} empty="Nothing published yet." />
        </Card>
      )}
    </div>
  );
}

function DocumentRows({
  documents,
  empty,
}: {
  documents: DocumentSummary[];
  empty: string;
}) {
  if (documents.length === 0) {
    return <p className="px-[18px] py-6 text-center text-base+ text-muted">{empty}</p>;
  }
  return (
    <ul className="px-[18px]">
      {documents.map((document) => (
        <li key={document.id} className="border-t border-line-soft first:border-0">
          <Link
            to={`/documents/${document.id}`}
            className="flex items-center gap-3 py-[10px] no-underline"
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate text-md font-semibold text-ink">
                {document.title}
              </span>
              <span className="mt-0.5 block text-sm text-muted-soft">
                {document.docNo} · {document.versionLabel}
              </span>
            </span>
            <ReviewBadge state={document.reviewState} days={document.daysUntilReview} />
            <StatusPill status={document.status} label={document.statusLabel} />
            <span className="hidden w-20 shrink-0 text-right text-sm+ text-muted-soft sm:block">
              {formatDate(document.updatedAt)}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}
